import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CartItem, CartItemGroup, CartItemType } from '../cart.model';
import { ProjectMetadata } from '../../../../../../../api/src/project/project.entity';
import { Utils } from '../../../../../../../api/src/project/project.utils';
import { CartService } from '../cart.service';
import { Project, ProjectsQuery, ProjectsService } from '../../project';
import { DeleteCartItemMode } from '../../../../pages/project/project.page';
import { SessionQuery } from "../../session";
import { Subject, takeUntil, tap } from "rxjs";

@Component({
	selector: 'app-cart-list-group-row',
	templateUrl: './cart-list-group-row.component.html',
	styleUrls: ['./cart-list-group-row.component.scss']
})
export class CartListGroupRowComponent implements OnInit, OnChanges, OnDestroy {
	readonly DeleteCartItemMode = DeleteCartItemMode;
	readonly CartItemType = CartItemType;

	public isGuestUser = false;
	private destroy$ = new Subject<void>();

	@Input() group: CartItemGroup;
	@Input() mode: 'list' | 'checkout' | 'post-checkout'; // Determines the template being used.
	@Input() showHeader: boolean = true;
	@Input() showThumbnail = true;
	@Input() showSizeCount: boolean = true;
	@Input() showGroupActions = true;
	@Input() showExport: boolean = false;
	@Input() showDelete: boolean = false;
	@Input() showExpandableDetails: boolean = false;
	@Input() showReview: boolean = false;
	@Input() useItemTranslations: boolean = false; // Use when we are not handling a real item, but a preview.
	@Input() highlightQuery: string; // Highlight a regex match in the group/item names
	@Input() actionInProgress: {group?: any, itemIds?: string[]} | null;

	@Input() forcePanelsOpen: boolean = false; // force panels open on init
	@Input() expandedItems: { [key: string]: boolean } = {};

	@Output() expandedChange: EventEmitter<{ key: string, state: boolean }> = new EventEmitter<{ key: string, state: boolean }>();
	@Output() goToGroup: EventEmitter<CartItemGroup> = new EventEmitter<CartItemGroup>();
	@Output() goToItem: EventEmitter<{ item: CartItem, group: CartItemGroup }> = new EventEmitter<{ item: CartItem, group: CartItemGroup }>();
	@Output() deleteItems: EventEmitter<{ items: CartItem[], mode: DeleteCartItemMode }> = new EventEmitter<{ items: CartItem[], mode: DeleteCartItemMode }>();
	@Output() deleteItem: EventEmitter<CartItem> = new EventEmitter<CartItem>();
	@Output() exportItem = new EventEmitter<CartItem>();
	@Output() groupAction = new EventEmitter<{action: string, group: CartItemGroup | any}>();
	@Output() itemAction = new EventEmitter<{action: string, item: CartItem | any}>();

	public combinedGroups: CartItemGroup[] = [];

	public detailsVisible = false;
	public checkoutTable;

	constructor(
		public cartService: CartService,
		private readonly projectsService: ProjectsService,
		private readonly projectsQuery: ProjectsQuery,
		private readonly sessionQuery: SessionQuery
	) { }

	async ngOnInit() {
		if (this.mode === 'checkout' && this.group?.type === CartItemType.VIDEO) {
			if (this.group.id){
				this.checkoutTable = await this.cartService.getItemCheckoutTable(this.group.items[0]?.projectId, this.group.id);
			}else {
				this.checkoutTable = await this.projectsService.getProjectCheckoutTable(this.group.items[0]?.projectId);
			}
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.group && changes.group.currentValue) {
			this.combinedGroups = this.cartService.combineItemsWithSubgroupItems(this.group)
				.sort((a, b) => a.name.localeCompare(b.name));
		}

		this.projectsQuery.selectActive()
			.pipe(
				takeUntil(this.destroy$),
				tap((project: Project) => {
					this.isGuestUser = this.sessionQuery.isGuestOfBusiness(project?.business?.id) && !this.sessionQuery.isAtLeastAdmin();
				})
			).subscribe();
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
	}

	getVideoMetadata(workspaceState: any): ProjectMetadata {
		return Utils.extractProjectMetadata(workspaceState);
	}

	getApprovedStatus(group: CartItemGroup): boolean{
		if (!group.items?.length){
			return false;
		}

		return group.items.map(item => item.approved).every(approved => approved);

	}

	emitDeletePackage(group: CartItemGroup) {
		const items = [
			...(group.subgroups?.map(subgroup => subgroup.items).flat() || []),
			...(group.items || [])
		];

		this.deleteItems.emit({
			items: items,
			mode: DeleteCartItemMode.Package
		});
	}

}
